import axios from 'axios'
import { normalize, schema } from 'normalizr'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'
import { mergeEntities, mergeAll } from '../utils/mergeNormalized'
import networkWrapper from '../utils/networkWrapper'
import api from '../api'

import selectors from '../selectors'
import snackbarActions from './snackbar'
import routerActions from './router'

const resource = new schema.Entity('resource')

const prefix = 'resource'

let performSearchTimeout = null

const initialState = {
  tree: normalize([], [resource]),
  list: normalize([], [resource]),
  finderList: normalize([], [resource]),
  search: '',
  finderSearch: '',
  displayAmount: 1,
  displayTimeUnit: 'day',
  finderLocation: {
    section: '',
    parentid: '',
  },
  clipboard: {
    type: null,
    resource: null,
  }
}

const reducers = {
  setTree: (state, action) => {
    state.tree = normalize(action.payload, [resource])
  },
  setList: (state, action) => {
    state.list = normalize(action.payload, [resource])
  },
  setFinderList: (state, action) => {
    state.finderList = normalize(action.payload, [resource])
  },
  setResource: (state, action) => {
    mergeEntities(state.list, normalize([action.payload], [resource]))
  },
  setSearch: (state, action) => {
    state.search = action.payload
  },
  setFinderSearch: (state, action) => {
    state.finderSearch = action.payload
  },
  setFinderLocation: (state, action) => {
    state.finderLocation = action.payload
  },
  setDisplayAmount: (state, action) => {
    state.displayAmount = action.payload
  },
  setDisplayTimeUnit: (state, action) => {
    state.displayTimeUnit = action.payload
  },
  setClipboard: (state, action) => {
    state.clipboard = action.payload
  },
  resetClipboard: (state, action) => {
    state.clipboard = {
      type: null,
      resource: null,
      id: null,
    }
  }
}

const loaders = {

  loadTree: (state) => axios.get(api.projectUrl(state, `/resource/tree`))
    .then(api.process),

  loadList: (state, { section, parentid }) => axios.get(api.projectUrl(state, `/resource/${section}/${parentid}`))
    .then(api.process),

  search: (state, { section, search }) => axios.get(api.projectUrl(state, `/resource/search/${section}`), {
    params: {
      search,
    }
  })
    .then(api.process),

  get: (state, id) => axios.get(api.projectUrl(state, `/resource/item/${id}`))
    .then(api.process),

  create: (state, { section, parentid, data }) => axios.post(api.projectUrl(state, `/resource/${section}/${parentid}`), data)
    .then(api.process),

  save: (state, { id, data }) => axios.put(api.projectUrl(state, `/resource/item/${id}`), data)
    .then(api.process),

  duplicate: (state, { id, parentid }) => axios.put(api.projectUrl(state, `/resource/item/${id}/duplicate/${parentid}`))
    .then(api.process),

  delete: (state, id) => axios.delete(api.projectUrl(state, `/resource/item/${id}`))
    .then(api.process),

}

const sideEffects = {
 
  loadTree: () => networkWrapper({
    prefix,
    name: 'loadTree',
    handler: async (dispatch, getState) => {
      const data = await loaders.loadTree(getState())
      dispatch(actions.setTree(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error listing the tree data: ${errorMessage}`))
    }
  }),

  loadList: ({
    section,
    parentid,
    finder = false,
  }) => networkWrapper({
    prefix,
    name: 'loadList',
    handler: async (dispatch, getState) => {
      dispatch(actions.setSearch(''))
      const data = await loaders.loadList(getState(), {
        section,
        parentid,
      })
      dispatch(finder ? actions.setFinderList(data) : actions.setList(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error listing the resource list: ${errorMessage}`))
    }
  }),

  get: (id) => networkWrapper({
    prefix,
    name: 'get',
    handler: async (dispatch, getState) => {
      if(id == 'new') return
      const data = await loaders.get(getState(), id)
      dispatch(actions.setResource(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error getting the resource: ${errorMessage}`))
    }
  }),

  save: ({
    section,
    parentid,
    type,
    id,
    data,
  }) => networkWrapper({
    prefix,
    name: 'save',
    handler: async (dispatch, getState) => {
      data.links = (data.links || []).map((link, i) => {
        return {
          order: i,
          meta: link.meta,
          resource: link.resource.id,
        }
      })
  
      let loader
  
      if(id == 'new') {
        const submitData = Object.assign({}, data, {
          type,
        })
        loader = () => loaders.create(getState(), {section, parentid, data: submitData})
      }
      else {
        loader = () => loaders.save(getState(), {id, data})
      }
      
      await loader()

      dispatch(actions.loadTree())
      dispatch(actions.loadList({section,parentid}))
      dispatch(snackbarActions.setSuccess(`resource ${ id == 'new' ? 'created' : 'saved' }`))
      dispatch(routerActions.navigateTo('resource.list', { section, parentid }))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error saving the resource: ${errorMessage}`))
    }
  }),

  delete: ({
    section,
    parentid,
    id,
  }) => networkWrapper({
    prefix,
    name: 'delete',
    handler: async (dispatch, getState) => {
      await loaders.delete(getState(), id)
      dispatch(snackbarActions.setSuccess(`resource deleted`))
      dispatch(actions.loadTree())
      dispatch(actions.loadList({section,parentid}))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error deleting the resource: ${errorMessage}`))
    }
  }),

  updateSearch: ({
    section,
    parentid,
    search,
    finder,
  }) => networkWrapper({
    prefix,
    name: 'updateSearch',
    handler: async (dispatch, getState) => {
      const setSearchAction = finder ?
        actions.setFinderSearch :
        actions.setSearch
      dispatch(setSearchAction(search))
      if(performSearchTimeout) clearTimeout(performSearchTimeout)
      if(!search) {
        dispatch(actions.loadList({
          section,
          parentid,
          finder,
        }))
        return
      }
      performSearchTimeout = setTimeout(() => {
        dispatch(actions.performSearch({
          finder,
          section,
          search,
        }))
      }, 300)
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error searching the resources: ${errorMessage}`))
    }
  }),

  performSearch: ({
    section,
    search,
    finder,
  }) => networkWrapper({
    prefix,
    name: 'performSearch',
    handler: async (dispatch, getState) => {
      const setResultsAction = finder ?
        actions.setFinderList :
        actions.setList

      const data = await loaders.search(getState(), {
        section,
        search,
      })
      dispatch(setResultsAction(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error searching the resources: ${errorMessage}`))
    }
  }),

  duplicate: ({
    section,
    parentid,
    resource,
    message = 'duplicated',
  }) => networkWrapper({
    prefix,
    name: 'duplicate',
    handler: async (dispatch, getState) => {
      await loaders.duplicate(getState(), {id: resource.id, parentid})
      dispatch(actions.loadTree())
      dispatch(actions.loadList({section, parentid}))
      dispatch(snackbarActions.setSuccess(`resource ${message}`))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error duplicating the resource: ${errorMessage}`))
    }
  }),

  cut: ({
    resource,
  }) => networkWrapper({
    prefix,
    name: 'cut',
    handler: async (dispatch, getState) => {
      dispatch(actions.setClipboard({
        type: 'cut',
        resource,
      }))
      dispatch(snackbarActions.setInfo(`cut to clipboard`))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error cutting the resource: ${errorMessage}`))
    }
  }),

  copy: ({
    resource,
  }) => networkWrapper({
    prefix,
    name: 'copy',
    handler: async (dispatch, getState) => {
      dispatch(actions.setClipboard({
        type: 'copy',
        resource,
      }))
      dispatch(snackbarActions.setInfo(`copied to clipboard`))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error copying the resource: ${errorMessage}`))
    }
  }),

  paste: ({
    section,
    parentid,
  }) => networkWrapper({
    prefix,
    name: 'paste',
    handler: async (dispatch, getState) => {
      const clipboard = selectors.resource.clipboard(getState())
      const folderPath = selectors.resource.folderPath(getState())

      const folderPathIds = folderPath.map(item => item.id)

      const {
        type,
        resource,
      } = clipboard

      if(!type) {
        dispatch(snackbarActions.setError(`nothing currently in the clipboard`))
        return
      }

      if(resource.section != section) {
        dispatch(snackbarActions.setError(`cannot paste a ${resource.section} into a ${section} folder`))
        return
      }

      if(type == 'cut') {

        if(folderPathIds.indexOf(resource.id) >= 0) {
          dispatch(snackbarActions.setError(`you cannot paste an item inside itself`))
          return false
        }

        await loaders.save(getState(), {
          id: resource.id, 
          data: {
            parent: parentid == 'root' ?
              null : 
              parentid
          }
        })

        dispatch(actions.loadTree())
        dispatch(actions.loadList({section,parentid}))
        dispatch(snackbarActions.setSuccess(`resource pasted`))
        dispatch(actions.resetClipboard({}))
      }
      else if(type == 'copy') {
        await dispatch(actions.duplicate({
          section,
          parentid,
          resource,
          message: 'pasted'
        }))
        dispatch(actions.resetClipboard({}))
      }
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error pasting the resource: ${errorMessage}`))
    }
  }),

}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export {
  actions,
  reducer,
}

export default actions